import { useEffect, useState, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Alert
} from '@mui/material';
import DateRangeSelector from '../../../components/DateRangeSelector';
import AdcashAvatyeDashboard from '../../../components/adcash/avatye/AdcashAvatyeDashboard';
import AdcashAvatyeRevenueSummaryTable from '../../../components/adcash/avatye/AdcashAvatyeRevenueSummaryTable';
import apiClient from '../../../utils/axiosClient';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import dayjs, { Dayjs } from 'dayjs';
import { 
    AdcashAvatyeRevenueSummary
} from '../../../types/adcash.types';

export default function AdcashAvatyeRevenue() {
  const [avatyeRevenueSummary, setAvatyeRevenueSummary] = useState<AdcashAvatyeRevenueSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().subtract(0, 'day'));
  const [initialized, setInitialized] = useState<boolean>(false);
  const [exchangeRate, setExchangeRate] = useState<number>(1230);

  useEffect(() => {
    setInitialized(true);
  }, []);

  const fetchAdcashAvatyeRevenueSummary = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    };

    apiClient.get(`/api/v1/stat/adcash/avatye/revenue/summary`, {
      params: params
    }).then((response) => {
      setAvatyeRevenueSummary(response.data);
      setErrorMessage('');
    }).catch((error) => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });

  }, [startDate, endDate]);

  useEffect(() => {
    if (initialized && startDate && endDate) {
        fetchAdcashAvatyeRevenueSummary();
    }
  }, [
    fetchAdcashAvatyeRevenueSummary,
    initialized,
    startDate,
    endDate
  ]);

  return (
    <Box
      sx={{
        opacity: loading ? 0.3 : 1,
        transition: 'opacity 0.3s ease',
        minHeight: '100vh'
      }}
    >
      <Box
        alignItems={'center'}
        display={'flex'}
      >
        <Box>
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onSearch={() => {
              if (startDate && endDate) {
                fetchAdcashAvatyeRevenueSummary();
              }
            }}
          />
        </Box>
      </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                ...PAGE_CARD_SX,
                width: '100%',
                maxWidth: '100%',
              }}
            >
              <AdcashAvatyeDashboard
                data={avatyeRevenueSummary?.revenueByApp || []}
                exchangeRate={exchangeRate}
              />
            </Box>
            <Box
              sx={{
                ...PAGE_CARD_SX,
                width: '100%',
                maxWidth: '100%',
              }}
            >
                <AdcashAvatyeRevenueSummaryTable
                    data={avatyeRevenueSummary?.revenueByApp || []} 
                    appList={avatyeRevenueSummary?.appList || []}
                    exchangeRate={exchangeRate}
                    onExchangeRateChange={setExchangeRate}
                />
              </Box>
          </>
        )}
    </Box>
  );
}
